import type { AssignmentEvent, ThreadItem, ThreadMessage } from '../types/whatsapp';

const key = (i: ThreadItem): string => `${i.kind}:${i.id}`;

/** Oldest first. At the same time a message comes before an event, then by id (the server's order). */
export const compareThreadItems = (a: ThreadItem, b: ThreadItem): number => {
  const ta = Date.parse(a.created_at);
  const tb = Date.parse(b.created_at);
  if (ta !== tb) return ta - tb;
  if (a.kind !== b.kind) return a.kind === 'message' ? -1 : 1;
  return a.id - b.id;
};

/**
 * Merges new items into the thread, de-duplicating by kind and id. A message that
 * is already there is replaced only by a copy with the same or a newer delivery status.
 */
export const mergeThread = (
  items: ThreadItem[],
  messages: ThreadMessage[] = [],
  events: AssignmentEvent[] = [],
): ThreadItem[] => {
  const byKey = new Map(items.map((i) => [key(i), i]));
  for (const incoming of [...messages, ...events] as ThreadItem[]) {
    const held = byKey.get(key(incoming));
    if (
      held &&
      held.kind === 'message' &&
      incoming.kind === 'message' &&
      Date.parse(held.status_updated_at) > Date.parse(incoming.status_updated_at)
    ) {
      continue;
    }
    byKey.set(key(incoming), incoming);
  }
  return [...byKey.values()].sort(compareThreadItems);
};

/** The id to send as after_message_id: the highest message id the browser holds, 0 when none. */
export const lastMessageId = (items: ThreadItem[]): number =>
  items.reduce((max, i) => (i.kind === 'message' && i.id > max ? i.id : max), 0);

/** The timestamp to send as `before` to load older items: that of the first item held. */
export const oldestCreatedAt = (items: ThreadItem[]): string | undefined => items[0]?.created_at;
